import React, { useState, useMemo } from 'react';
import {
  PlusCircle, Search, Filter, Eye, Trash2, CheckSquare, Square,
  Archive, UserCheck, ArrowRight, X,
} from 'lucide-react';
import {
  TASK_TYPES, STATUSES, PRIORITIES, SITES, STORES, USERS,
} from '../constants';
import { isOverdue, formatDate } from '../utils/helpers';
import { StatusBadge, PriorityBadge, RiskBadge, FilterSelect } from './Shared';

export function TaskTable({ tasks, navigateTo, currentUser, userRole, deleteTask, selectedIds, toggleSelect, toggleAll }) {
  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 font-medium">
            <tr>
              <th className="px-4 py-3 text-left w-10">
                <button onClick={toggleAll} className="text-slate-400 hover:text-indigo-600">
                  <CheckSquare className="w-4 h-4" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">任务编号/标题</th>
              <th className="px-4 py-3 text-left">类型</th>
              <th className="px-4 py-3 text-left">店铺/站点</th>
              <th className="px-4 py-3 text-left">负责人</th>
              <th className="px-4 py-3 text-left">优先级</th>
              <th className="px-4 py-3 text-left">状态</th>
              <th className="px-4 py-3 text-left">风险</th>
              <th className="px-4 py-3 text-left">截止时间</th>
              <th className="px-4 py-3 text-center">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {tasks.length === 0 && <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">暂无任务</td></tr>}
            {tasks.map(t => (
              <tr key={t.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3">
                  <button onClick={() => toggleSelect(t.id)} className="text-slate-400 hover:text-indigo-600">
                    {selectedIds?.has(t.id) ? <CheckSquare className="w-4 h-4 text-indigo-600" /> : <Square className="w-4 h-4" />}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-800">{t.title}</div>
                  <div className="text-xs text-slate-400 font-mono mt-0.5">{t.id}</div>
                  {t.tags && t.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {t.tags.map(tag => <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{tag}</span>)}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-600">{t.type}</td>
                <td className="px-4 py-3 text-slate-600">{t.store} / {t.site}</td>
                <td className="px-4 py-3 text-slate-600">
                  <div className="flex items-center">
                    <div className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-semibold mr-1.5">{t.assignee.slice(0, 1)}</div>
                    {t.assignee}
                    {t.collaborators.includes(currentUser) && t.assignee !== currentUser && <span className="ml-1 text-xs text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">协作</span>}
                  </div>
                </td>
                <td className="px-4 py-3"><PriorityBadge priority={t.priority} /></td>
                <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                <td className="px-4 py-3"><RiskBadge level={t.riskLevel} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center">
                    <span className={isOverdue(t.dueDate, t.status) ? 'text-rose-600 font-medium' : 'text-slate-600'}>{formatDate(t.dueDate)}</span>
                    {isOverdue(t.dueDate, t.status) && <AlertCircle className="w-3.5 h-3.5 text-rose-500 ml-1" />}
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center space-x-1">
                    <button onClick={() => navigateTo('detail', t.id)} className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"><Eye className="w-4 h-4" /></button>
                    {(userRole?.canDeleteAny || (userRole?.canDeleteOwn && t.assignee === currentUser)) && (
                      <button onClick={() => { if (confirm('确定删除此任务？')) deleteTask(t.id); }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function TaskList({ tasks, navigateTo, currentUser, userRole, deleteTask, batchUpdateStatus, batchAssign, batchArchive, title = '全部任务' }) {
  const [filters, setFilters] = useState({ type: '', status: '', priority: '', assignee: '', site: '', store: '', overdue: '', needLeoApproval: '', riskLevel: '', search: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBatch, setShowBatch] = useState(false);
  const [batchAction, setBatchAction] = useState('');
  const [batchValue, setBatchValue] = useState('');

  const filtered = useMemo(() => {
    return tasks.filter(t => {
      if (filters.type && t.type !== filters.type) return false;
      if (filters.status && t.status !== filters.status) return false;
      if (filters.priority && t.priority !== filters.priority) return false;
      if (filters.assignee && t.assignee !== filters.assignee) return false;
      if (filters.site && t.site !== filters.site) return false;
      if (filters.store && t.store !== filters.store) return false;
      if (filters.overdue === 'yes' && !isOverdue(t.dueDate, t.status)) return false;
      if (filters.overdue === 'no' && isOverdue(t.dueDate, t.status)) return false;
      if (filters.needLeoApproval === 'yes' && !t.needLeoApproval) return false;
      if (filters.needLeoApproval === 'no' && t.needLeoApproval) return false;
      if (filters.riskLevel && t.riskLevel !== filters.riskLevel) return false;
      if (filters.search && !t.title.toLowerCase().includes(filters.search.toLowerCase()) && !t.id.toLowerCase().includes(filters.search.toLowerCase())) return false;
      return true;
    });
  }, [tasks, filters]);

  function updateFilter(key, value) { setFilters(prev => ({ ...prev, [key]: value })); }
  function resetFilters() { setFilters({ type: '', status: '', priority: '', assignee: '', site: '', store: '', overdue: '', needLeoApproval: '', riskLevel: '', search: '' }); }

  function toggleSelect(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    const all = filtered.map(t => t.id);
    const allSelected = all.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      all.forEach(id => {
        if (allSelected) next.delete(id); else next.add(id);
      });
      return next;
    });
  }

  function executeBatch() {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (batchAction === 'status') batchUpdateStatus(ids, batchValue);
    if (batchAction === 'assign') batchAssign(ids, batchValue);
    if (batchAction === 'archive') batchArchive(ids);
    setSelectedIds(new Set());
    setShowBatch(false);
    setBatchAction('');
    setBatchValue('');
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-slate-800">{title}</h1>
        <button onClick={() => navigateTo('create')} className="btn-primary"><PlusCircle className="w-4 h-4 mr-2" /> 新建任务</button>
      </div>

      <div className="card p-4 mb-4">
        <div className="flex items-center space-x-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="搜索任务标题或编号..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" value={filters.search} onChange={e => updateFilter('search', e.target.value)} />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className={`inline-flex items-center px-3 py-2 text-sm border rounded-lg transition-colors ${showFilters ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            <Filter className="w-4 h-4 mr-2" /> 筛选
          </button>
          {selectedIds.size > 0 && (
            <button onClick={() => setShowBatch(true)} className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg">
              批量操作 ({selectedIds.size})
            </button>
          )}
        </div>
        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 pt-4 border-t border-slate-100">
            <FilterSelect label="任务类型" value={filters.type} onChange={v => updateFilter('type', v)} options={['', ...TASK_TYPES]} />
            <FilterSelect label="状态" value={filters.status} onChange={v => updateFilter('status', v)} options={['', ...STATUSES.map(s => s.key)]} labels={STATUSES.reduce((a, s) => { a[s.key] = s.label; return a; }, { '': '全部' })} />
            <FilterSelect label="优先级" value={filters.priority} onChange={v => updateFilter('priority', v)} options={['', ...PRIORITIES.map(p => p.key)]} labels={PRIORITIES.reduce((a, p) => { a[p.key] = p.label; return a; }, { '': '全部' })} />
            <FilterSelect label="负责人" value={filters.assignee} onChange={v => updateFilter('assignee', v)} options={['', ...USERS]} />
            <FilterSelect label="站点" value={filters.site} onChange={v => updateFilter('site', v)} options={['', ...SITES]} />
            <FilterSelect label="店铺" value={filters.store} onChange={v => updateFilter('store', v)} options={['', ...STORES]} />
            <FilterSelect label="是否超时" value={filters.overdue} onChange={v => updateFilter('overdue', v)} options={['', 'yes', 'no']} labels={{ '': '全部', yes: '已超时', no: '未超时' }} />
            <FilterSelect label="需审批" value={filters.needLeoApproval} onChange={v => updateFilter('needLeoApproval', v)} options={['', 'yes', 'no']} labels={{ '': '全部', yes: '是', no: '否' }} />
            <FilterSelect label="风险等级" value={filters.riskLevel} onChange={v => updateFilter('riskLevel', v)} options={['', 'high', 'medium', 'low']} labels={{ '': '全部', high: '高风险', medium: '中风险', low: '低风险' }} />
          </div>
        )}
        {showFilters && <div className="mt-3 flex justify-end"><button onClick={resetFilters} className="text-sm text-slate-500 hover:text-indigo-600">重置筛选</button></div>}
      </div>

      {showBatch && (
        <div className="card p-4 mb-4 bg-indigo-50 border-indigo-200">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-indigo-800">批量操作 ({selectedIds.size} 项已选择)</span>
            <button onClick={() => setShowBatch(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex items-center space-x-3">
            <select className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white" value={batchAction} onChange={e => { setBatchAction(e.target.value); setBatchValue(''); }}>
              <option value="">选择操作</option>
              <option value="status">修改状态</option>
              <option value="assign">分配负责人</option>
              <option value="archive">归档</option>
            </select>
            {batchAction === 'status' && (
              <select className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white" value={batchValue} onChange={e => setBatchValue(e.target.value)}>
                <option value="">选择状态</option>
                {STATUSES.filter(s => s.key !== 'archived').map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            )}
            {batchAction === 'assign' && (
              <select className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white" value={batchValue} onChange={e => setBatchValue(e.target.value)}>
                <option value="">选择负责人</option>
                {USERS.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            )}
            <button onClick={executeBatch} disabled={!batchAction || (batchAction !== 'archive' && !batchValue)} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50">执行</button>
          </div>
        </div>
      )}

      <TaskTable
        tasks={filtered}
        navigateTo={navigateTo}
        currentUser={currentUser}
        userRole={userRole}
        deleteTask={deleteTask}
        selectedIds={selectedIds}
        toggleSelect={toggleSelect}
        toggleAll={toggleAll}
      />
    </div>
  );
}

function AlertCircle(props) {
  return (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>
  );
}
